import axiosInstance from '../axios'
import type { CatalogMaterial } from '../types/calculadoraDeMateriales.ts'
import type { CatalogPickOption } from '../components/Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsCatalogPick.ts'

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify')
  return { headers: { Authorization: `Bearer ${authToken}` } }
}

export type CatalogMaterialSearchItem = {
  id: number
  name: string
  unit?: string
  price?: number | null
  categoryId: number
  categoryName: string
}

export type CategoryStatRow = {
  id: number | string
  name: string
  materialCount: number
}

export type CategoryStatsPayload = {
  rows: CategoryStatRow[]
  totalMaterials: number | null
}

export type CategoryListRow = {
  id: number | string
  name: string
}

export type CategoryMaterialsPage = {
  category: { id: number; name: string }
  items: CatalogMaterialSearchItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

function unwrapData<T>(body: unknown): T | null {
  if (body == null || typeof body !== 'object') return null
  const o = body as Record<string, unknown>
  const inner = o.data
  if (inner !== undefined) return inner as T
  return body as T
}

function unwrapItems(body: unknown): CatalogMaterialSearchItem[] {
  const data = unwrapData<{ items?: unknown }>(body)
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    if (Array.isArray(data)) return normalizeSearchItems(data)
    return []
  }
  const items = (data as Record<string, unknown>).items
  return Array.isArray(items) ? normalizeSearchItems(items) : []
}

function normalizeSearchItems(raw: unknown[]): CatalogMaterialSearchItem[] {
  const out: CatalogMaterialSearchItem[] = []
  for (const row of raw) {
    if (row == null || typeof row !== 'object' || Array.isArray(row)) continue
    const o = row as Record<string, unknown>
    const id = Number(o.id)
    const name = typeof o.name === 'string' ? o.name.trim() : ''
    const categoryId = Number(o.categoryId ?? o.category_id)
    const categoryName =
      typeof o.categoryName === 'string'
        ? o.categoryName.trim()
        : typeof o.category_name === 'string'
          ? o.category_name.trim()
          : ''
    if (!Number.isFinite(id) || !name || !Number.isFinite(categoryId) || !categoryName) continue
    const unit = typeof o.unit === 'string' ? o.unit : undefined
    const priceRaw = o.price
    const price =
      priceRaw != null && Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null
    out.push({ id, name, unit, price, categoryId, categoryName })
  }
  return out
}

export function searchItemToCatalogPick(item: CatalogMaterialSearchItem): CatalogPickOption {
  return {
    categoryId: String(item.categoryId),
    categoryName: item.categoryName,
    materialId: String(item.id),
    materialName: item.name,
  }
}

export function searchItemsToCatalogPicks(items: CatalogMaterialSearchItem[]): CatalogPickOption[] {
  return items.map(searchItemToCatalogPick)
}

export function searchItemToCatalogMaterial(
  item: CatalogMaterialSearchItem
): CatalogMaterial & { category: string; categoryId: string; categoryName: string } {
  return {
    id: item.id,
    name: item.name,
    unit: item.unit ?? 'u',
    price: item.price ?? undefined,
    category: item.categoryName,
    categoryId: String(item.categoryId),
    categoryName: item.categoryName,
  }
}

function coerceCategoryRowsArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  if (raw == null || typeof raw !== 'object') return []
  const o = raw as Record<string, unknown>
  const nested = unwrapData<unknown>(raw)
  if (nested !== raw && nested != null) {
    const fromNested = coerceCategoryRowsArray(nested)
    if (fromNested.length > 0) return fromNested
  }
  for (const key of ['categories', 'stats', 'items', 'data'] as const) {
    const candidate = o[key]
    if (Array.isArray(candidate)) return candidate
  }
  return []
}

function normalizeCategoryList(raw: unknown): CategoryListRow[] {
  const unwrapped = unwrapData<unknown>(raw) ?? raw
  const arr = coerceCategoryRowsArray(unwrapped)
  const out: CategoryListRow[] = []
  for (const item of arr) {
    if (item == null || typeof item !== 'object' || Array.isArray(item)) continue
    const o = item as Record<string, unknown>
    const name = typeof o.name === 'string' ? o.name.trim() : ''
    const idRaw = o.id
    let id: number | string | null = null
    if (typeof idRaw === 'number' && Number.isFinite(idRaw)) id = idRaw
    else if (typeof idRaw === 'string' && idRaw.trim()) id = idRaw.trim()
    else if (idRaw != null && Number.isFinite(Number(idRaw))) id = Number(idRaw)
    if (id == null || !name) continue
    out.push({ id, name })
  }
  return out.sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'))
}

function parseCategoryRowId(o: Record<string, unknown>): number | string | null {
  const idRaw = o.id ?? o.categoryId ?? o.category_id
  if (typeof idRaw === 'number' && Number.isFinite(idRaw)) return idRaw
  if (typeof idRaw === 'string' && idRaw.trim()) return idRaw.trim()
  if (idRaw != null && Number.isFinite(Number(idRaw))) return Number(idRaw)
  return null
}

function normalizeCategoryStats(raw: unknown): CategoryStatRow[] {
  const unwrapped = unwrapData<unknown>(raw) ?? raw
  const arr = coerceCategoryRowsArray(unwrapped)
  const out: CategoryStatRow[] = []
  for (const item of arr) {
    if (item == null || typeof item !== 'object' || Array.isArray(item)) continue
    const o = item as Record<string, unknown>
    const id = parseCategoryRowId(o)
    if (id == null) continue
    const name =
      typeof o.name === 'string'
        ? o.name.trim()
        : typeof o.categoryName === 'string'
          ? o.categoryName.trim()
          : typeof o.category_name === 'string'
            ? o.category_name.trim()
            : ''
    const materialCount = Number(
      o.materialCount ?? o.material_count ?? o.materialsCount ?? o.count ?? o.total ?? 0
    )
    out.push({
      id,
      name,
      materialCount: Number.isFinite(materialCount) && materialCount >= 0 ? materialCount : 0,
    })
  }
  return out
}

export function extractCatalogTotalFromStatsBody(body: unknown): number | null {
  const unwrapped = unwrapData<unknown>(body) ?? body
  if (unwrapped == null || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) return null
  const o = unwrapped as Record<string, unknown>
  const total = Number(
    o.totalMaterials ??
      o.total_materials ??
      o.totalMaterialCount ??
      o.total_material_count ??
      o.catalogTotal ??
      o.total
  )
  return Number.isFinite(total) && total >= 0 ? total : null
}

let categoriesCache: CategoryListRow[] | null = null
let categoryStatsCache: CategoryStatRow[] | null = null
let featuredCache: CatalogMaterialSearchItem[] | null = null

export function clearCatalogMaterialsCaches() {
  categoriesCache = null
  categoryStatsCache = null
  featuredCache = null
}

export async function fetchCatalogCategories(): Promise<CategoryListRow[]> {
  if (categoriesCache && categoriesCache.length > 0) return categoriesCache
  const res = await axiosInstance.get('/category/all', getAuthHeaders())
  const rows = normalizeCategoryList(res.data)
  if (rows.length > 0) categoriesCache = rows
  return rows
}

export async function fetchCategoryStats(): Promise<CategoryStatsPayload> {
  if (categoryStatsCache && categoryStatsCache.length > 0) {
    const summed = sumCategoryMaterialCounts(categoryStatsCache)
    return { rows: categoryStatsCache, totalMaterials: summed > 0 ? summed : null }
  }
  const res = await axiosInstance.get('/category/stats', getAuthHeaders())
  const rows = normalizeCategoryStats(res.data)
  if (rows.length > 0) categoryStatsCache = rows
  return {
    rows,
    totalMaterials: extractCatalogTotalFromStatsBody(res.data),
  }
}

export async function searchCatalogMaterials(params: {
  q: string
  limit?: number
  excludeIds?: number[]
  categoryId?: number | string
}): Promise<CatalogMaterialSearchItem[]> {
  const q = params.q.trim()
  if (q.length < 2) return []
  const limit = params.limit ?? 40
  const searchParams: Record<string, string | number> = { q, limit }
  if (params.categoryId != null && params.categoryId !== '') {
    searchParams.categoryId = Number(params.categoryId)
  }
  const exclude = (params.excludeIds ?? []).filter((id) => Number.isFinite(id) && id > 0).slice(0, 500)
  if (exclude.length > 0) {
    searchParams.excludeIds = exclude.join(',')
  }
  const res = await axiosInstance.get('/materials/search', {
    ...getAuthHeaders(),
    params: searchParams,
  })
  return unwrapItems(res.data)
}

export async function fetchFeaturedCatalogMaterials(limit = 50): Promise<CatalogMaterialSearchItem[]> {
  if (featuredCache && featuredCache.length > 0) return featuredCache.slice(0, limit)
  const res = await axiosInstance.get('/materials/featured', {
    ...getAuthHeaders(),
    params: { limit },
  })
  const items = unwrapItems(res.data)
  featuredCache = items
  return items.slice(0, limit)
}

export async function batchCatalogMaterialsByIds(
  ids: Array<number | string>
): Promise<CatalogMaterialSearchItem[]> {
  const numeric = ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
  if (numeric.length === 0) return []
  const res = await axiosInstance.post(
    '/materials/batch',
    { ids: numeric },
    getAuthHeaders()
  )
  return unwrapItems(res.data)
}

export async function fetchCategoryMaterialsPage(
  categoryId: number | string,
  params?: { page?: number; pageSize?: number; q?: string }
): Promise<CategoryMaterialsPage> {
  const page = params?.page ?? 1
  const pageSize = params?.pageSize ?? 50
  const q = params?.q?.trim() ?? ''
  const res = await axiosInstance.get(`/category/materials/${encodeURIComponent(String(categoryId))}`, {
    ...getAuthHeaders(),
    params: { page, pageSize, ...(q.length > 0 ? { q } : {}) },
  })
  const data = unwrapData<Record<string, unknown>>(res.data)
  if (data == null) {
    return {
      category: { id: Number(categoryId), name: '' },
      items: [],
      page: 1,
      pageSize,
      total: 0,
      totalPages: 0,
    }
  }
  const catRaw = data.category
  const category =
    catRaw != null && typeof catRaw === 'object' && !Array.isArray(catRaw)
      ? {
          id: Number((catRaw as Record<string, unknown>).id ?? categoryId),
          name: String((catRaw as Record<string, unknown>).name ?? '').trim(),
        }
      : { id: Number(categoryId), name: '' }
  const items = normalizeSearchItems(Array.isArray(data.items) ? (data.items as unknown[]) : [])
  const resolvedPageSize = Number(data.pageSize ?? data.page_size) || pageSize
  const total = Number(data.total)
  const resolvedTotal = Number.isFinite(total) && total >= 0 ? total : items.length
  let totalPages = Number(data.totalPages ?? data.total_pages)
  if (!Number.isFinite(totalPages) || totalPages <= 0) {
    totalPages =
      resolvedTotal > 0 ? Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize)) : items.length >= resolvedPageSize ? 2 : 1
  }
  return {
    category,
    items,
    page: Number(data.page) || page,
    pageSize: resolvedPageSize,
    total: resolvedTotal,
    totalPages,
  }
}

export function sumCategoryMaterialCounts(stats: CategoryStatRow[]): number {
  return stats.reduce((sum, row) => sum + (row.materialCount > 0 ? row.materialCount : 0), 0)
}
